import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { DsIconButton } from '@/ds/button'
import { DsIcons } from '@/ds/icons'
import { DsBreadcrumbs, type DsBreadcrumb } from '@/ds/breadcrumbs'

const pageWidth = { narrow: 'max-w-[760px]', default: 'max-w-[1040px]', wide: 'max-w-[1280px]', full: 'max-w-[1600px]' }

export interface DsPageProps {
  title: string
  subtitle?: string
  /** Show a back button; a function overrides the default history back. */
  onBack?: (() => void) | true
  backLabel?: string
  actions?: ReactNode
  leading?: ReactNode
  banner?: ReactNode
  bottomBar?: ReactNode
  spacing?: 'sm' | 'md' | 'lg'
  wide?: boolean
  /** Desktop content width: narrow 760 (forms) · default 1040 · wide 1280 (tables) · full 1600 (dashboards). */
  width?: 'narrow' | 'default' | 'wide' | 'full'
  /** Trail above the title on desktop; phones use the back button. */
  breadcrumbs?: DsBreadcrumb[]
  tabs?: ReactNode
  children: ReactNode
  className?: string
}

// The mobile collapsing large title has no web equivalent; the title is a plain heading.
export function DsPage({ title, subtitle, onBack, backLabel = 'Back', actions, leading, banner, bottomBar, spacing = 'md', wide, width, breadcrumbs, tabs, children, className }: DsPageProps) {
  const navigate = useNavigate()
  const back = onBack === true ? () => navigate(-1) : onBack
  const w = width ?? (wide ? 'wide' : 'default')
  return (
    <div className={cn('mx-auto flex w-full flex-col', pageWidth[w], className)}>
      {breadcrumbs && <DsBreadcrumbs items={breadcrumbs} className="mb-2" />}
      <header className={cn('flex items-start gap-2', tabs ? 'mb-3' : 'mb-4 md:mb-6')}>
        {back && <DsIconButton icon={DsIcons.leftArrow} semanticLabel={backLabel} onClick={back} className="-ml-2" />}
        {leading}
        <div className="min-w-0 flex-1 pt-1.5">
          <h1 className="font-display text-title1 text-foreground md:text-display">{title}</h1>
          {subtitle && <p className="mt-1 text-body text-muted-foreground">{subtitle}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2 pt-1">{actions}</div>}
      </header>
      {tabs && <div className="mb-4 md:mb-6">{tabs}</div>}
      {banner && <div className="mb-4">{banner}</div>}
      <div className={cn('flex flex-col', { sm: 'gap-2', md: 'gap-4', lg: 'gap-6' }[spacing], bottomBar && 'pb-24')}>{children}</div>
      {bottomBar && <div className="fixed inset-x-0 bottom-16 z-10 md:bottom-0 md:left-[var(--shell-sidebar,16rem)]">{bottomBar}</div>}
    </div>
  )
}
